import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Cuánto dura el tratamiento con SAINT® TMS?",
    answer:
      "El tratamiento con SAINT® dura 5 días consecutivos. Cada día incluye 10 sesiones de unos 10 minutos cada una, con descansos de 50 minutos. Antes del tratamiento, se requieren una evaluación psiquiátrica y una fMRI. Tras el protocolo de 5 días, visitas de seguimiento monitorizan la respuesta. El proceso completo dura más que solo la semana de tratamiento.",
  },
  {
    question: "¿Cuántos días tengo que estar en la clínica para SAINT®?",
    answer:
      "Debe estar en la clínica durante 5 días consecutivos. Cada día incluye 10 sesiones con descansos de 50 minutos, de modo que pasará la mayor parte de cada día en la clínica. Antes de la semana de tratamiento también necesita una evaluación psiquiátrica y una fMRI, que se programan por separado.",
  },
  {
    question: "¿Cuánto dura cada sesión de SAINT®?",
    answer:
      "Cada sesión de SAINT® dura unos 10 minutos. Con 10 sesiones por día y descansos de 50 minutos entre ellas, los pacientes pasan la mayor parte de cada día de tratamiento en la clínica. Los descansos de 50 minutos permiten que el cerebro responda al patrón de estimulación.",
  },
  {
    question: "¿Con qué rapidez puedo empezar SAINT®?",
    answer:
      "Tras una evaluación psiquiátrica que confirme la elegibilidad, los siguientes pasos son programar una fMRI y planificar la semana de tratamiento. Los pacientes de pago directo pueden empezar a menudo en unos días. La verificación del seguro, si aplica, puede tardar más. Contacte con Neuro Wellness para hablar de la programación.",
  },
  {
    question: "¿Es SAINT® más rápida que la TMS convencional?",
    answer:
      "Sí, en cuanto al calendario de tratamiento. SAINT® se completa en 5 días, mientras que la TMS convencional dura 4–6 semanas de visitas diarias. Sin embargo, SAINT® también requiere una fMRI y una evaluación previas. El proceso total sigue siendo más corto que un ciclo de TMS convencional.",
  },
];

export const esHowLongTake = makeEsArticle({
  slug: "how-long-does-saint-tms-treatment-take",
  title: "¿Cuánto dura el tratamiento con SAINT® TMS? | Neuro Wellness",
  description:
    "El tratamiento con SAINT® TMS dura 5 días consecutivos: 10 sesiones por día, unos 10 minutos cada una, con descansos de 50 minutos. Conozca el calendario completo, incluidas la evaluación y la resonancia.",
  h1: "¿Cuánto dura el tratamiento con SAINT® TMS?",
  breadcrumbLabel: "Cuánto dura SAINT®",
  shortAnswer:
    "El tratamiento con SAINT® TMS dura 5 días consecutivos. Cada día incluye 10 sesiones de unos 10 minutos cada una, con descansos de unos 50 minutos entre sesiones, de modo que los pacientes pasan la mayor parte de cada día en la clínica. Antes de la semana de tratamiento se requieren una evaluación psiquiátrica y una resonancia magnética funcional (fMRI), que se programan con antelación. Tras el protocolo de 5 días, las visitas de seguimiento monitorizan la respuesta. La semana de tratamiento son 5 días, pero el proceso completo — evaluación, fMRI, tratamiento y seguimiento — abarca un periodo más largo.",
  keyTakeaways: [
    "El tratamiento con SAINT® dura 5 días consecutivos.",
    "Cada día: 10 sesiones de ~10 minutos, con descansos de 50 minutos.",
    "Los pacientes pasan la mayor parte de cada día de tratamiento en la clínica.",
    "Antes del tratamiento se requieren una evaluación psiquiátrica y una fMRI.",
    "Las visitas de seguimiento monitorizan la respuesta tras el protocolo de 5 días.",
  ],
  sections: [
    {
      heading: "La semana de tratamiento de 5 días",
      paragraphs: [
        "El tratamiento con SAINT® se administra durante 5 días consecutivos. Cada día incluye 10 sesiones de unos 10 minutos cada una, separadas por descansos de unos 50 minutos. Con este calendario, los pacientes pasan la mayor parte de cada día en la clínica.",
        "Los descansos de 50 minutos son una parte deliberada del protocolo — permiten que el cerebro responda al patrón de estimulación entre sesiones. En los 5 días completos, esto totaliza 50 sesiones.",
      ],
    },
    {
      heading: "Antes del tratamiento: evaluación y resonancia",
      paragraphs: [
        "Antes de que comience la semana de tratamiento, se requieren dos pasos:",
      ],
      list: [
        "Evaluación psiquiátrica — un psiquiatra revisa su diagnóstico, historial de medicación y estado de salud general para confirmar la elegibilidad.",
        "Resonancia magnética funcional (fMRI) — una resonancia mapea su conectividad individual para identificar el objetivo de estimulación personalizado.",
      ],
      paragraphsAfter: [
        "Estos se programan antes de la semana de tratamiento y deben completarse antes del primer día de tratamiento. El tiempo entre su consulta y el inicio del tratamiento depende de la disponibilidad de programación y, si procede, de la verificación del seguro.",
      ],
    },
    {
      heading: "Después del tratamiento: seguimiento",
      paragraphs: [
        "Tras el protocolo de 5 días, las visitas de seguimiento con su psiquiatra monitorizan la respuesta y analizan un plan de mantenimiento. El ensayo controlado aleatorizado de 2022 midió los resultados a las 4 semanas. Un plan de mantenimiento puede incluir medicación, psicoterapia y la posibilidad de otro ciclo de SAINT® si fuera necesario.",
        "La frecuencia del seguimiento depende de la respuesta individual y del criterio clínico. Algunas visitas de seguimiento pueden realizarse de forma remota.",
      ],
    },
    {
      heading: "El calendario completo",
      paragraphs: ["El proceso completo de SAINT® incluye:"],
      list: [
        "Consulta y evaluación psiquiátrica (programadas con antelación).",
        "Resonancia fMRI (programada antes de la semana de tratamiento).",
        "Semana de tratamiento de 5 días (10 sesiones por día, la mayor parte de cada día en la clínica).",
        "Visitas de seguimiento (a partir de después del tratamiento, según sea necesario).",
        "Plan de mantenimiento (medicación, terapia, monitorización).",
      ],
    },
    {
      heading: "Qué significa para los pacientes",
      paragraphs: [
        "La semana de tratamiento en sí son 5 días, pero el proceso completo — evaluación, fMRI, tratamiento y seguimiento — abarca un periodo más largo. Los pacientes de pago directo pueden empezar el tratamiento a los pocos días de su consulta. La verificación del seguro, si procede, puede añadir tiempo.",
        "Si está planificando en torno al trabajo, los viajes u otros compromisos, comente el calendario completo con el equipo de Neuro Wellness durante su consulta para poder planificar en consecuencia.",
      ],
      links: [
        {
          to: "/es/learn/what-to-expect-during-saint-tms",
          text: "Ver una guía día por día de los 5 días de SAINT®",
        },
        {
          to: "/es/saint",
          text: "Más información sobre SAINT® en Neuro Wellness",
        },
        {
          to: "/es/contactus",
          text: "Solicitar una consulta",
          suffix: " o llame al 954-827-9793.",
        },
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
